import { useZoomService } from '@src/hooks/useZoomService';
import { useDatoPerson, useStore } from '@src/stores-v2/StoreContext';
import CheckIcon from '@src/svgs/CheckIcon';
import CloseIcon from '@src/svgs/CloseIcon';
import SearchIcon from '@src/svgs/SearchIcon';
import { LogManager } from '@src/utility/Logger';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import type { ZoomRecording } from '@src/services/ZoomService';

const logger = LogManager.getLogger('component:ZoomRecordingsList');

const PAGE_SIZE = 10;

interface Props {
  onSelectRecording: (recording: ZoomRecording) => void;
  refreshTrigger?: boolean;
  zoomPersonId?: string;
}

const ZoomRecordingsList = ({
  onSelectRecording,
  refreshTrigger,
  zoomPersonId,
}: Props) => {
  const [allLoadedRecordings, setAllLoadedRecordings] = useState<
    ZoomRecording[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedRecordingId, setExpandedRecordingId] = useState<string | null>(
    null,
  );
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const fetchingPagesRef = useRef<Set<number>>(new Set());
  const nextPageRef = useRef(1);
  const [videoUrls, setVideoUrls] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<'browse' | 'search'>('browse');
  const [searchResults, setSearchResults] = useState<ZoomRecording[]>([]);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedFileIndex, setSelectedFileIndex] = useState<
    Record<string, number>
  >({});
  const { userIdentityStore } = useStore();
  const datoPerson = useDatoPerson();
  const zoomService = useZoomService();

  // Use zoomPersonId prop if provided (the person with zoom connection), otherwise fall back to datoPerson
  const effectivePersonId = zoomPersonId || datoPerson?.person?.id;

  const fetchPage = useCallback(
    async (page: number, refresh?: boolean) => {
      // Prevent duplicate fetches for the same page
      if (fetchingPagesRef.current.has(page)) {
        return;
      }

      fetchingPagesRef.current.add(page);

      if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
        loadingMoreRef.current = true;
      }
      setError(null);

      try {
        const result = await zoomService.listRecordingsPaginated(
          effectivePersonId,
          userIdentityStore.currentIdentity?.email,
          page,
          PAGE_SIZE,
          refresh,
        );

        if (result.ok) {
          const { recordings, total, hasMore: more } = result.value;
          if (page === 1) {
            setAllLoadedRecordings(recordings);
            nextPageRef.current = 2;
          } else {
            // Deduplicate by recording ID to prevent duplicates from race conditions
            setAllLoadedRecordings(prev => {
              const existingIds = new Set(prev.map(r => r.id));
              const newRecordings = recordings.filter(
                r => !existingIds.has(r.id),
              );
              return [...prev, ...newRecordings];
            });
            nextPageRef.current = page + 1;
          }
          setTotalCount(total);
          setHasMore(more);
        } else {
          setError(result.error.message);
          logger.error(
            'Failed to fetch Zoom recordings:',
            result.error.message,
          );
        }
      } finally {
        fetchingPagesRef.current.delete(page);
        setIsLoading(false);
        setIsLoadingMore(false);
        loadingMoreRef.current = false;
      }
    },
    [zoomService, effectivePersonId, userIdentityStore.currentIdentity?.email],
  );

  useEffect(() => {
    // Reset refs on mount
    fetchingPagesRef.current.clear();
    nextPageRef.current = 1;
    fetchPage(1);

    return () => {
      fetchingPagesRef.current.clear();
    };
  }, []);

  // Refetch when refreshTrigger changes to true
  useEffect(() => {
    if (refreshTrigger) {
      fetchingPagesRef.current.clear();
      nextPageRef.current = 1;
      fetchPage(1, true);
    }
  }, [refreshTrigger]);

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatSize = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024);
    if (gb > 1) {
      return `${gb.toFixed(1)} GB`;
    }
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(0)} MB`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMp4Files = useCallback(
    (recording: ZoomRecording) =>
      recording.recording_files.filter(
        f => f.file_type === 'MP4' && f.status === 'completed',
      ),
    [],
  );

  const formatRecordingType = (type: string): string => {
    const map: Record<string, string> = {
      shared_screen_with_speaker_view: 'Screen + Speaker',
      shared_screen_with_gallery_view: 'Screen + Gallery',
      active_speaker: 'Active Speaker',
      speaker_view: 'Speaker View',
      gallery_view: 'Gallery View',
      shared_screen: 'Screen Only',
      audio_only: 'Audio Only',
    };
    return map[type] || type;
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleFileNav = (recordingId: string, newIndex: number) => {
    setSelectedFileIndex(prev => ({ ...prev, [recordingId]: newIndex }));
    setVideoUrls(prev => {
      const next = { ...prev };
      delete next[recordingId];
      return next;
    });
    setExpandedRecordingId(null);
  };

  const filteredRecordings = useMemo(() => {
    const source = viewMode === 'search' ? searchResults : allLoadedRecordings;
    if (searchTerm.trim() === '') return source;
    const term = searchTerm.toLowerCase();
    return source.filter(
      rec =>
        rec.topic.toLowerCase().includes(term) ||
        formatDate(rec.start_time).toLowerCase().includes(term),
    );
  }, [searchTerm, allLoadedRecordings, searchResults, viewMode]);

  const handleSelect = (recording: ZoomRecording) => {
    setSelectedId(recording.id);
    const mp4Files = getMp4Files(recording);
    const fileIndex = selectedFileIndex[recording.id] ?? 0;
    const selectedMp4 = mp4Files[fileIndex];
    if (selectedMp4) {
      const filteredRecording = {
        ...recording,
        recording_files: [
          selectedMp4,
          ...recording.recording_files.filter(f => f.file_type !== 'MP4'),
        ],
      };
      onSelectRecording(filteredRecording);
    } else {
      onSelectRecording(recording);
    }
  };

  const handlePreviewClick = (
    recording: ZoomRecording,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.stopPropagation();
    if (!recording.has_video) return;

    // Toggle collapse if already expanded
    if (expandedRecordingId === recording.id) {
      setExpandedRecordingId(null);
      return;
    }

    setExpandedRecordingId(recording.id);

    // Use cached URL if available
    if (videoUrls[recording.id]) return;

    // Build proxy URL synchronously - the backend resolves the Zoom token server-side
    const mp4Files = getMp4Files(recording);
    const fileIndex = selectedFileIndex[recording.id] ?? 0;
    const mp4File = mp4Files[fileIndex];
    const downloadUrl =
      mp4File?.download_url || recording.video_play_url || recording.share_url;

    if (downloadUrl) {
      const proxyUrl = zoomService.getProxyVideoUrl({
        downloadUrl,
        personId: effectivePersonId,
        email: userIdentityStore.currentIdentity?.email,
        password: recording.password,
      });
      setVideoUrls(prev => ({ ...prev, [recording.id]: proxyUrl }));
    }
  };

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const nearBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight < 50;

      // Use nextPageRef to avoid stale closure issues with currentPage
      if (nearBottom && hasMore && !loadingMoreRef.current) {
        fetchPage(nextPageRef.current);
      }
    },
    [hasMore, fetchPage],
  );

  const handleSearchSubmit = useCallback(async () => {
    const trimmed = searchTerm.trim();
    if (!trimmed) return;

    setIsSearching(true);
    const result = await zoomService.searchRecordings(
      effectivePersonId,
      userIdentityStore.currentIdentity?.email,
      trimmed,
    );

    if (result.ok) {
      setSearchResults(result.value.recordings);
      setActiveSearchQuery(trimmed);
      setViewMode('search');
    } else {
      setError(result.error.message);
    }
    setIsSearching(false);
  }, [
    searchTerm,
    zoomService,
    effectivePersonId,
    userIdentityStore.currentIdentity?.email,
  ]);

  const handleBackToBrowse = useCallback(() => {
    setViewMode('browse');
    setSearchTerm('');
    setActiveSearchQuery('');
    setSearchResults([]);
  }, []);

  const handleRefresh = useCallback(() => {
    handleBackToBrowse();
    fetchPage(1, true);
  }, [fetchPage, handleBackToBrowse]);

  const footerText = useMemo(() => {
    const loaded = allLoadedRecordings.length;
    const displayTotal = Math.max(loaded, totalCount);

    if (searchTerm.trim()) {
      const matching = filteredRecordings.length;
      return `Showing ${matching} of ${loaded} loaded recordings`;
    }

    if (!hasMore && loaded >= displayTotal) {
      return `Showing all ${loaded} recordings`;
    }

    return `Showing ${loaded} of ${displayTotal} recordings`;
  }, [
    allLoadedRecordings.length,
    filteredRecordings.length,
    totalCount,
    hasMore,
    searchTerm,
  ]);

  if (isLoading) {
    return (
      <Container>
        <LoadingMessage>Loading your Zoom recordings...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>
          Failed to load recordings: {error}
          <RetryButton onClick={handleRefresh}>Try Again</RetryButton>
        </ErrorMessage>
      </Container>
    );
  }

  if (allLoadedRecordings.length === 0 && totalCount === 0) {
    return (
      <Container>
        <EmptyMessage>
          No cloud recordings found in your Zoom account.
          <HelpText>
            Make sure you have cloud recording enabled in your Zoom account.
          </HelpText>
        </EmptyMessage>
      </Container>
    );
  }

  return (
    <Container>
      <SearchContainer>
        <SearchInputWrapper>
          <SearchIcon fillColor="var(--text-muted)" />
          <SearchInput
            type="text"
            placeholder="Filter loaded recordings or press Enter to search all"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSearchSubmit();
            }}
          />
          {searchTerm && (
            <ClearButton
              aria-label="Clear search"
              onClick={() => {
                if (viewMode === 'search') {
                  handleBackToBrowse();
                } else {
                  setSearchTerm('');
                }
              }}
            >
              <CloseIcon width={10} height={10} />
            </ClearButton>
          )}
        </SearchInputWrapper>
        <RefreshButton onClick={handleRefresh}>
          <RefreshIcon />
        </RefreshButton>
      </SearchContainer>

      {viewMode === 'search' && (
        <SearchResultsHeader>
          <BackButton onClick={handleBackToBrowse}>
            ← Back to all recordings
          </BackButton>
          <SearchResultsCount>
            {filteredRecordings.length} result
            {filteredRecordings.length !== 1 ? 's' : ''} for "
            {activeSearchQuery}"
          </SearchResultsCount>
        </SearchResultsHeader>
      )}

      {isSearching ? (
        <SearchOverlay>
          <Spinner />
        </SearchOverlay>
      ) : filteredRecordings.length === 0 && searchTerm ? (
        <NoResults>
          No recordings found
          {viewMode === 'search'
            ? ` for "${activeSearchQuery}"`
            : ` matching "${searchTerm}"`}
        </NoResults>
      ) : (
        <RecordingsList
          onScroll={viewMode === 'browse' ? handleScroll : undefined}
        >
          {filteredRecordings.map(recording => {
            const mp4Files = getMp4Files(recording);
            const currentIndex = selectedFileIndex[recording.id] ?? 0;
            const currentMp4 = mp4Files[currentIndex];

            return (
              <RecordingItem
                key={recording.id}
                selected={selectedId === recording.id}
                onClick={() => handleSelect(recording)}
              >
                <RecordingContent>
                  <RecordingInfo>
                    <RecordingTitle>{recording.topic}</RecordingTitle>
                    <RecordingMeta>
                      <MetaItem bgColor="var(--destructive)">
                        {formatDate(recording.start_time)}
                      </MetaItem>
                      <MetaItem>{formatDuration(recording.duration)}</MetaItem>
                      <MetaItem>
                        {currentMp4
                          ? formatSize(currentMp4.file_size)
                          : formatSize(recording.total_size)}
                      </MetaItem>
                    </RecordingMeta>
                    {currentMp4 && (
                      <RecordingFileInfo>
                        <span>
                          {formatRecordingType(currentMp4.recording_type)}
                        </span>
                        {currentMp4.recording_start && (
                          <>
                            <RecordingFileInfoDot>·</RecordingFileInfoDot>
                            <span>
                              {formatTime(currentMp4.recording_start)}
                              {currentMp4.recording_end &&
                                ` – ${formatTime(currentMp4.recording_end)}`}
                            </span>
                          </>
                        )}
                      </RecordingFileInfo>
                    )}
                    {mp4Files.length > 1 && (
                      <RecordingCount>
                        {mp4Files.length} recordings
                      </RecordingCount>
                    )}
                    {recording.has_video && (
                      <PreviewButton
                        onClick={e => handlePreviewClick(recording, e)}
                      >
                        {expandedRecordingId === recording.id
                          ? '▲ Hide preview'
                          : '▶ Preview'}
                      </PreviewButton>
                    )}
                  </RecordingInfo>
                </RecordingContent>

                {selectedId === recording.id && (
                  <SelectedIcon>
                    <CheckIcon
                      fillColor="var(--background)"
                      strokeWidth="2"
                      width="10"
                      height="10"
                    />
                  </SelectedIcon>
                )}

                {expandedRecordingId === recording.id && (
                  <PreviewContainer>
                    {videoUrls[recording.id] ? (
                      <VideoPlayer
                        controls
                        crossOrigin="anonymous"
                        src={videoUrls[recording.id]}
                      />
                    ) : (
                      <LoadingText>Failed to load video</LoadingText>
                    )}
                  </PreviewContainer>
                )}

                {mp4Files.length > 1 && (
                  <RecordingNav onClick={e => e.stopPropagation()}>
                    <NavButton
                      disabled={currentIndex === 0}
                      onClick={() =>
                        handleFileNav(recording.id, currentIndex - 1)
                      }
                    >
                      ‹
                    </NavButton>
                    <NavLabel>
                      Recording {currentIndex + 1} of {mp4Files.length}
                    </NavLabel>
                    <NavButton
                      disabled={currentIndex === mp4Files.length - 1}
                      onClick={() =>
                        handleFileNav(recording.id, currentIndex + 1)
                      }
                    >
                      ›
                    </NavButton>
                  </RecordingNav>
                )}
              </RecordingItem>
            );
          })}
          {isLoadingMore && viewMode === 'browse' && (
            <SpinnerContainer>
              <Spinner />
            </SpinnerContainer>
          )}
        </RecordingsList>
      )}

      {viewMode === 'browse' &&
        (filteredRecordings.length > 0 || allLoadedRecordings.length > 0) && (
          <FooterNote>{footerText}</FooterNote>
        )}
    </Container>
  );
};

const RefreshIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
    <path d="M8 16H3v5" />
  </svg>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SearchInputWrapper = styled.div`
  flex: 1;
  box-sizing: border-box;
  height: 36px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 8px 0 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus-within {
    border-color: var(--accent);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 0;
  border: none;
  outline: none;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-style: normal;
  font-weight: var(--font-weight-regular);
  line-height: 140%;
  background: var(--background);

  &::placeholder {
    color: var(--text-muted);
  }
`;

const ClearButton = styled.button`
  background: transparent;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0 8px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: var(--text-secondary);
  }
`;

const RefreshButton = styled.button`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px 10px;
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--text-muted);
  border-radius: 6px;
  font-size: var(--text-sm);
  cursor: pointer;
  transition: all var(--transition-normal);
  height: 36px;

  &:hover {
    background: var(--primary-tint-light);
    border-color: var(--primary-hover);
    color: var(--primary-hover);
  }
`;

const NoResults = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: var(--text-muted);
  font-size: 14px;
`;

const RecordingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--surface-subtle);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-muted);
  }
`;

const RecordingItem = styled.div<{ selected: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background: ${props =>
    props.selected ? 'var(--primary-tint-light)' : 'var(--surface-subtle)'};
  border: 1px solid
    ${props => (props.selected ? 'var(--primary-hover)' : 'var(--border)')};
  border-radius: 8px;
  cursor: pointer;
  transition: all var(--transition-normal);
  position: relative;
  gap: 12px;

  &:hover {
    border-color: var(--primary-hover);
    background: var(--primary-tint-subtle);
  }
`;

const RecordingInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RecordingTitle = styled.div`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
`;

const RecordingMeta = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div<{ bgColor?: string }>`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  &::before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => props.bgColor || 'var(--text-muted)'};
    margin-right: 4px;
  }
`;

const RecordingFileInfo = styled.div`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
  font-size: 11px;
  gap: 0;
`;

const RecordingFileInfoDot = styled.span`
  margin: 0 6px;
`;

const RecordingCount = styled.div`
  color: var(--text-muted);
  font-size: 11px;
`;

const RecordingNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-top: 1px solid var(--border);
  padding-top: 8px;
`;

const NavButton = styled.button`
  background: var(--sidebar-hover);
  border: none;
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 20px;
  cursor: pointer;
  padding: 4px 12px;

  &:disabled {
    color: var(--border);
    cursor: default;
  }

  &:not(:disabled):hover {
    color: var(--text-secondary);
    background: var(--sidebar-active);
  }
`;

const NavLabel = styled.span`
  color: var(--text-secondary);
  font-size: var(--text-xs);
  flex-grow: 1;
  text-align: center;
`;

const RecordingContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

const PreviewButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary-hover);
  font-size: 11px;
  cursor: pointer;
  padding: 4px 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const SelectedIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 24px;
  background: var(--primary-hover);
  color: var(--background);
  position: absolute;
  top: 20px;
  right: 20px;
`;

const LoadingMessage = styled.div`
  color: var(--text-secondary);
  text-align: center;
  padding: 40px;
`;

const ErrorMessage = styled.div`
  color: var(--destructive);
  text-align: center;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  color: var(--primary-hover);
  border: 1px solid var(--primary-hover);
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: var(--primary-tint-light);
  }
`;

const EmptyMessage = styled.div`
  color: var(--text-secondary);
  text-align: center;
  padding: 40px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: var(--text-muted);
  max-width: 300px;
  margin: 0 auto;
`;

const FooterNote = styled.div`
  font-size: 11px;
  color: var(--text-muted);
  text-align: center;
  padding-top: 8px;
`;

const PreviewContainer = styled.div`
  width: 100%;
  margin-top: 12px;
  border-radius: 8px;
  overflow: hidden;
  background: var(--background);
`;

const VideoPlayer = styled.video`
  width: 100%;
  max-height: 200px;
  display: block;
`;

const LoadingText = styled.div`
  padding: 24px;
  text-align: center;
  color: var(--text-muted);
  font-size: 13px;
`;

const SearchResultsHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary-hover);
  font-size: var(--text-xs);
  cursor: pointer;
  padding: 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const SearchResultsCount = styled.div`
  color: var(--text-secondary);
  font-size: var(--text-xs);
`;

const SearchOverlay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 40px 0;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px 0;
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 2px solid var(--border);
  border-top-color: var(--primary-hover);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default ZoomRecordingsList;
